import React, { useState, useEffect, useContext } from "react";
import Papa from "papaparse";
import styled from "styled-components";
import _ from "lodash";
import { AuthContext } from "../../../../../auth-context";
import { DashboardContext } from "../../../dashboard-context";
import Modal from "../../../../../components/modal";
import { addPortalAccount, loadProfile } from "../../../../../api";
import iconCheck from "../../../../../images/iconCheck.png"
import iconAlert from "../../../../../images/iconOver.png"

const ImportText = styled.label`
  color: ${(props) => props.theme.color.primary};
  cursor: pointer;
  display: block;
  text-decoration: underline;
  justify-self: flex-start;
  margin-right: auto;
  input {
    display: none;
  }
`;

const ImportButton = styled.label`
  background: ${(props) =>
    props.warning ? "#f44336" : props.theme.button.background};
  border: none;
  border-radius: ${(props) => props.theme.button.borderRadius};
  color: ${(props) => props.theme.button.color};
  cursor: pointer;
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  height: 44px;
  width: 180px;
  margin: 0 auto;
  padding: 12px 18px;
  position: relative;
  text-align: center;
  transition: 0.2s ease all;

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 900px) {
    font-size: 0.875rem;
    height: 40px;
  }

  input {
    display: none;
  }
`;

const TableContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 0px 8px 0px #e1e2e6;
  max-height: 380px;
  margin: 0 auto;
  overflow: auto;
  position: relative;
`;

const Table = styled.table`
  background: #fff;
  border-spacing: 0px;
  width: 100%;
`;

const TableHead = styled.thead`
  color: #fff;
`;

const TableBody = styled.tbody``;

const TableHeadRow = styled.tr``;

const TableBodyRow = styled.tr`
  &:hover {
    background: #f2f2f2;
  }
`;

const HeadCell = styled.th`
  background: #070c29 !important;
  font-family: ${(props) => props.theme.font.primary};
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 1px;
  min-width: ${(props) => (props.bigWidth ? "160px" : "100px")};
  padding: 8px;
  position: sticky;
  text-align: left;
  top: 0;
  z-index: 30;
`;

const DataCell = styled.td`
  border-bottom: 1px solid #e8e8e8;
  font-family: ${(props) =>
    props.number ? props.theme.font.primary : props.theme.font.secondary};
  font-size: 0.9rem;
  padding: 4px 8px;
  text-align: left;

  span {
    display: flex;
    align-items: center;
  }
  img {
    margin-right: 6px;
    width: 18px;
  }
`;

const emailIsValid = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const ImportCSV = () => {
  const { user, updateUser } = useContext(AuthContext);
  const { openSnackbar } = useContext(DashboardContext);
  const [file, setFile] = useState("");
  const [data, setData] = useState([]);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(false);

  useEffect(() => {
    if (file) {
      const updateData = (result) => {
        let nameKey, emailKey;
        Object.keys(result.data[0]).forEach((key) => {
          const formattedKey = key.toLowerCase();
          if (formattedKey.includes("name")) {
            nameKey = key;
          } else if (formattedKey.includes("email")) {
            emailKey = key;
          }
        });

        if(!nameKey || !emailKey) {
          return;
        }

        let data = result.data
          .map((client) => {
            return {
              name: client[nameKey].trim(),
              email_address: client[emailKey].trim(),
            };
          })
          .filter(
            (client) =>
              client.name &&
              client.email_address &&
              emailIsValid(client.email_address)
          )
          .sort((a, b) => a.name.localeCompare(b.name));
        data = _.uniqBy(data, "email_address"); // Remove duplicate emails
        setData(data);
      };

      Papa.parse(file, {
        skipEmptyLines: true,
        complete: updateData,
        header: true,
      });
    }
  }, [file]);

  useEffect(() => {
    if (!show)
      setTimeout(() => {
        setFile("");
        setData([]);
        setResult(false);
      }, 1000);
  }, [show]);

  const submit = () => {
    setLoading(true);
    addPortalAccount(user.id, data)
      .then((res) => {
        setLoading(false);
        setResult(res.data);
        loadProfile(user.id)
          .then((res) => {
            if (res.data.profile.active === 1) {
              updateUser(res.data.profile);
            }
          })
          .catch((err) => {
            console.log(err);
          });
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        openSnackbar("An error has occured.", true);
      });
  };

  const renderImportView = () => {
    return (
      <>
        <p>
          To import multiple clients at once, upload a .csv file with client's
          names and email addresses.
        </p>
        <ImportButton>
          Import .csv file
          <input
            type="file"
            accept=".csv"
            name="file"
            onChange={(e) => setFile(e.target.files[0])}
            style={{ display: "none" }}
          />
        </ImportButton>
      </>
    );
  };

  const renderTableView = () => {
    const list = result || data;
    let successCount = 0;
    if (result) {
      result.forEach((client) => {
        if (client.success) {
          successCount = successCount += 1;
        }
      });
    }
    return (
      <>
        {result ? 
          <p>{`${successCount} out of ${result.length} clients were successfully imported.`}</p>
        :
          <>
            <p>After importing, a total of {data.length} accounts were detected.</p>
            {(data.length === 0) && 
              <p>Please be sure that the header of the .csv file includes "name" and "email" when importing.</p>
            }
          </>
        }
        <TableContainer>
          <Table>
            <TableHead>
              <TableHeadRow>
                <HeadCell>Name</HeadCell>
                <HeadCell>Email Address</HeadCell>
                <HeadCell bigWidth>Status</HeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {list.map((client, index) => (
                <TableBodyRow key={index}>
                  <DataCell>{client.name}</DataCell>
                  <DataCell>{client.email_address}</DataCell>
                  <DataCell bigWidth>{!!(client.result) ? <span><img src={client.success ? iconCheck : iconAlert} alt="status-icon" /> {client.result}</span> : ""}</DataCell>
                </TableBodyRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <>
      <ImportText onClick={() => setShow(true)}>
        Import clients with a .csv file
      </ImportText>
      <Modal
        maxWidth={file ? "md" : null}
        title="Import clients"
        open={show}
        onClose={() => setShow(false)}
        hideAction={file === "" || !!result}
        isLoading={loading}
        disabled={loading}
        submitLabel="Add clients"
        handleSubmit={submit}
      >
        {file === "" ? renderImportView() : renderTableView()}
      </Modal>
    </>
  );
};
